import React, { useEffect, useRef, useState } from 'react';
import SeoScoreDetails from './SeoScoreDetails';
import './SeoScoreMeter.css';

const RADIUS = 7;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function tierFor(overall) {
  if (overall >= 80) return 'good';
  if (overall >= 60) return 'fair';
  return 'poor';
}

/**
 * Subtle circular SEO score indicator for the editor control bar. Shows the
 * overall score in a compact ring; clicking expands a detailed breakdown of
 * how the score was produced.
 */
export default function SeoScoreMeter({ score }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  if (!score) return null;

  const tier = tierFor(score.overall);
  const dashOffset = CIRCUMFERENCE * (1 - score.overall / 100);

  return (
    <div className="seo-score-meter" ref={wrapRef}>
      <button
        type="button"
        className={`seo-score-meter__button seo-score-meter__button--${tier}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`SEO score ${score.overall} out of 100. Click for details.`}
        title={`SEO score: ${score.overall}/100`}
      >
        <svg className="seo-score-meter__ring" width="18" height="18" viewBox="0 0 18 18">
          <circle className="seo-score-meter__track" cx="9" cy="9" r={RADIUS} fill="none" strokeWidth="2.5" />
          <circle
            className="seo-score-meter__progress"
            cx="9"
            cy="9"
            r={RADIUS}
            fill="none"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 9 9)"
          />
        </svg>
        <span className="seo-score-meter__value">{score.overall}</span>
        <span className="seo-score-meter__label">SEO</span>
      </button>

      {open && (
        <div className="seo-score-meter__popover">
          <SeoScoreDetails score={score} />
        </div>
      )}
    </div>
  );
}
